// =============================
// File: src/components/admin/DocContentBuilder.jsx
// =============================
import React, { useState, useRef } from 'react';
import {
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Type,
  Image as ImageIcon,
  List,
  Heading,
  Minus,
  GripVertical,
  Upload
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function DocContentBuilder({ blocks, onBlocksChange }) {
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [uploadingImageForBlock, setUploadingImageForBlock] = useState(null);

  function addBlock(type) {
    const newBlock = {
      id: `temp-${Date.now()}`,
      block_type: type,
      order_index: blocks.length,
      content: type === 'list' ? JSON.stringify([]) : '',
      image_url: null,
      image_caption: '',
      image_alt: '',
      heading_level: type === 'heading' ? 2 : null,
      list_type: type === 'list' ? 'bullet' : null
    };

    onBlocksChange([...blocks, newBlock]);
    setShowAddMenu(false);
  }

  function updateBlock(index, updates) {
    const updatedBlocks = [...blocks];
    updatedBlocks[index] = { ...updatedBlocks[index], ...updates };
    onBlocksChange(updatedBlocks);
  }

  function deleteBlock(index) {
    const updatedBlocks = blocks.filter((_, i) => i !== index);
    onBlocksChange(updatedBlocks);
  }

  function moveBlock(index, direction) {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === blocks.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const updatedBlocks = [...blocks];
    [updatedBlocks[index], updatedBlocks[newIndex]] = [updatedBlocks[newIndex], updatedBlocks[index]];
    onBlocksChange(updatedBlocks);
  }

  async function handleImageUpload(file, blockIndex) {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Tipo de archivo no válido. Use PNG, JPG, GIF o WEBP');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 5MB');
      return;
    }

    try {
      setUploadingImageForBlock(blockIndex);

      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post(`${API_URL}/api/docs/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        updateBlock(blockIndex, { image_url: response.data.image_url });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error al subir la imagen');
    } finally {
      setUploadingImageForBlock(null);
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-slate-900 font-montserrat">
          Contenido del Documento
        </h2>
        <button
          onClick={() => setShowAddMenu(!showAddMenu)}
          className="px-4 py-2 bg-brand-primary text-white rounded-lg font-semibold font-montserrat hover:bg-brand-primary/90 transition-all flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Agregar Bloque
        </button>
      </div>

      {/* Add Block Menu */}
      {showAddMenu && (
        <div className="mb-6 p-4 bg-slate-50 rounded-lg border-2 border-brand-primary/30">
          <p className="text-sm font-semibold text-slate-700 font-montserrat mb-3">
            Selecciona el tipo de bloque:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <button
              onClick={() => addBlock('heading')}
              className="p-3 bg-white hover:bg-brand-primary/10 border border-slate-200 hover:border-brand-primary rounded-lg transition-all flex items-center gap-2 font-montserrat"
            >
              <Heading className="w-4 h-4 text-brand-primary" />
              <span className="text-sm font-semibold">Título</span>
            </button>
            <button
              onClick={() => addBlock('paragraph')}
              className="p-3 bg-white hover:bg-brand-primary/10 border border-slate-200 hover:border-brand-primary rounded-lg transition-all flex items-center gap-2 font-montserrat"
            >
              <Type className="w-4 h-4 text-brand-primary" />
              <span className="text-sm font-semibold">Párrafo</span>
            </button>
            <button
              onClick={() => addBlock('image')}
              className="p-3 bg-white hover:bg-brand-primary/10 border border-slate-200 hover:border-brand-primary rounded-lg transition-all flex items-center gap-2 font-montserrat"
            >
              <ImageIcon className="w-4 h-4 text-brand-primary" />
              <span className="text-sm font-semibold">Imagen</span>
            </button>
            <button
              onClick={() => addBlock('list')}
              className="p-3 bg-white hover:bg-brand-primary/10 border border-slate-200 hover:border-brand-primary rounded-lg transition-all flex items-center gap-2 font-montserrat"
            >
              <List className="w-4 h-4 text-brand-primary" />
              <span className="text-sm font-semibold">Lista</span>
            </button>
            <button
              onClick={() => addBlock('divider')}
              className="p-3 bg-white hover:bg-brand-primary/10 border border-slate-200 hover:border-brand-primary rounded-lg transition-all flex items-center gap-2 font-montserrat"
            >
              <Minus className="w-4 h-4 text-brand-primary" />
              <span className="text-sm font-semibold">Separador</span>
            </button>
          </div>
        </div>
      )}

      {/* Blocks List */}
      <div className="space-y-4">
        {blocks.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
            <Type className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-montserrat mb-4">
              No hay bloques de contenido todavía
            </p>
            <button
              onClick={() => setShowAddMenu(true)}
              className="px-4 py-2 bg-brand-primary text-white rounded-lg font-semibold font-montserrat hover:bg-brand-primary/90 transition-all"
            >
              Agregar Primer Bloque
            </button>
          </div>
        ) : (
          blocks.map((block, index) => (
            <BlockEditor
              key={block.id}
              block={block}
              index={index}
              isFirst={index === 0}
              isLast={index === blocks.length - 1}
              onUpdate={(updates) => updateBlock(index, updates)}
              onDelete={() => deleteBlock(index)}
              onMoveUp={() => moveBlock(index, 'up')}
              onMoveDown={() => moveBlock(index, 'down')}
              onImageUpload={(file) => handleImageUpload(file, index)}
              isUploading={uploadingImageForBlock === index}
            />
          ))
        )}
      </div>
    </div>
  );
}

// Individual block editor component
function BlockEditor({
  block,
  index,
  isFirst,
  isLast,
  onUpdate,
  onDelete,
  onMoveUp,
  onMoveDown,
  onImageUpload,
  isUploading
}) {
  const fileInputRef = useRef(null);
  const [listItems, setListItems] = useState(
    block.block_type === 'list' ? JSON.parse(block.content || '[]') : []
  );

  function handleListChange(items) {
    setListItems(items);
    onUpdate({ content: JSON.stringify(items) });
  }

  function addListItem() {
    const newItems = [...listItems, ''];
    handleListChange(newItems);
  }

  function updateListItem(itemIndex, value) {
    const newItems = [...listItems];
    newItems[itemIndex] = value;
    handleListChange(newItems);
  }

  function deleteListItem(itemIndex) {
    const newItems = listItems.filter((_, i) => i !== itemIndex);
    handleListChange(newItems);
  }

  const blockTypeLabels = {
    heading: 'Título',
    paragraph: 'Párrafo',
    image: 'Imagen',
    list: 'Lista',
    divider: 'Separador'
  };

  const blockTypeIcons = {
    heading: Heading,
    paragraph: Type,
    image: ImageIcon,
    list: List,
    divider: Minus
  };

  const Icon = blockTypeIcons[block.block_type];

  return (
    <div
      className="bg-slate-50 border-2 border-slate-200 rounded-lg p-4 hover:border-brand-primary/50 transition-all"
      tabIndex={block.block_type === 'image' ? 0 : undefined}
      onDragOver={block.block_type === 'image' ? (event) => event.preventDefault() : undefined}
      onDrop={block.block_type === 'image' ? (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files?.[0];
        if (file) onImageUpload(file);
      } : undefined}
      onPaste={block.block_type === 'image' ? (event) => {
        const file = Array.from(event.clipboardData?.items || [])
          .find(item => item.type?.startsWith('image/'))?.getAsFile();
        if (file) {
          event.preventDefault();
          onImageUpload(file);
        }
      } : undefined}
    >
      {/* Block Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <GripVertical className="w-5 h-5 text-slate-400" />
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-brand-primary" />
            <span className="text-sm font-bold text-slate-700 font-montserrat">
              #{index + 1} - {blockTypeLabels[block.block_type]}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={onMoveUp}
            disabled={isFirst}
            className="p-1.5 hover:bg-slate-200 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Mover arriba"
          >
            <ChevronUp className="w-4 h-4 text-slate-600" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={isLast}
            className="p-1.5 hover:bg-slate-200 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="Mover abajo"
          >
            <ChevronDown className="w-4 h-4 text-slate-600" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 hover:bg-red-100 rounded transition-colors ml-2"
            title="Eliminar"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      </div>

      {/* Block Content Editor */}
      <div className="space-y-3">
        {block.block_type === 'heading' && (
          <>
            <div>
              <label className="block text-xs font-semibold text-slate-600 font-montserrat mb-2">
                Nivel del título
              </label>
              <select
                value={block.heading_level || 2}
                onChange={(e) => onUpdate({ heading_level: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat text-sm"
              >
                <option value={1}>H1 (Principal)</option>
                <option value={2}>H2 (Sección)</option>
                <option value={3}>H3 (Subsección)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 font-montserrat mb-2">
                Texto del título
              </label>
              <input
                type="text"
                value={block.content || ''}
                onChange={(e) => onUpdate({ content: e.target.value })}
                placeholder="Escribe el título aquí..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat"
              />
            </div>
          </>
        )}

        {block.block_type === 'paragraph' && (
          <div>
            <label className="block text-xs font-semibold text-slate-600 font-montserrat mb-2">
              Contenido del párrafo
            </label>
            <textarea
              value={block.content || ''}
              onChange={(e) => onUpdate({ content: e.target.value })}
              rows={4}
              placeholder="Escribe el contenido del párrafo aquí..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat resize-none"
            />
          </div>
        )}

        {block.block_type === 'image' && (
          <>
            <div>
              <label className="block text-xs font-semibold text-slate-600 font-montserrat mb-2">
                Imagen
              </label>

              {block.content && !block.image_url && (
                <div className="mb-3 rounded-lg border border-purple-200 bg-purple-50 p-3">
                  <p className="text-[10px] font-black uppercase tracking-wide text-purple-600">Marcador creado por IA</p>
                  <p className="mt-1 text-xs font-semibold text-purple-900">{block.content}</p>
                </div>
              )}
              
              {block.image_url ? (
                <div className="relative">
                  <img
                    src={block.image_url}
                    alt="Preview"
                    className="w-full rounded-lg border border-slate-300"
                  />
                  <button
                    onClick={() => onUpdate({ image_url: null })}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) onImageUpload(file);
                    }}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                      event.preventDefault();
                      const file = event.dataTransfer.files?.[0];
                      if (file) onImageUpload(file);
                    }}
                    onPaste={(event) => {
                      const file = Array.from(event.clipboardData?.items || [])
                        .find(item => item.type?.startsWith('image/'))?.getAsFile();
                      if (file) {
                        event.preventDefault();
                        onImageUpload(file);
                      }
                    }}
                    disabled={isUploading}
                    className="w-full py-8 border-2 border-dashed border-slate-300 rounded-lg hover:border-brand-primary hover:bg-brand-primary/5 transition-all flex flex-col items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploading ? (
                      <>
                        <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm font-semibold text-brand-primary font-montserrat">
                          Subiendo imagen...
                        </span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-slate-400" />
                        <span className="text-sm font-semibold text-slate-600 font-montserrat">
                          Click, arrastra o pega una imagen
                        </span>
                        <span className="text-xs text-slate-500 font-montserrat">
                          PNG, JPG, GIF o WEBP (máx. 5MB)
                        </span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {block.image_url && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 font-montserrat mb-2">
                    Texto alternativo (Alt)
                  </label>
                  <input
                    type="text"
                    value={block.image_alt || ''}
                    onChange={(e) => onUpdate({ image_alt: e.target.value })}
                    placeholder="Describe la imagen para accesibilidad..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 font-montserrat mb-2">
                    Descripción (opcional)
                  </label>
                  <input
                    type="text"
                    value={block.image_caption || ''}
                    onChange={(e) => onUpdate({ image_caption: e.target.value })}
                    placeholder="Descripción que aparecerá debajo de la imagen..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat text-sm"
                  />
                </div>
              </>
            )}
          </>
        )}

        {block.block_type === 'list' && (
          <>
            <div>
              <label className="block text-xs font-semibold text-slate-600 font-montserrat mb-2">
                Tipo de lista
              </label>
              <select
                value={block.list_type || 'bullet'}
                onChange={(e) => onUpdate({ list_type: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat text-sm"
              >
                <option value="bullet">Viñetas (•)</option>
                <option value="numbered">Numerada (1, 2, 3...)</option>
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-slate-600 font-montserrat">
                  Items de la lista
                </label>
                <button
                  onClick={addListItem}
                  className="text-xs font-semibold text-brand-primary hover:text-brand-primary/80 font-montserrat flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Agregar item
                </button>
              </div>

              <div className="space-y-2">
                {listItems.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500 font-montserrat">
                      {itemIndex + 1}.
                    </span>
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => updateListItem(itemIndex, e.target.value)}
                      placeholder={`Item ${itemIndex + 1}`}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary font-montserrat text-sm"
                    />
                    <button
                      onClick={() => deleteListItem(itemIndex)}
                      className="p-2 hover:bg-red-100 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                ))}

                {listItems.length === 0 && (
                  <p className="text-xs text-slate-500 font-montserrat text-center py-4">
                    No hay items todavía. Click "Agregar item" para comenzar.
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {block.block_type === 'divider' && (
          <div className="py-4">
            <hr className="border-slate-300" />
            <p className="text-xs text-slate-500 font-montserrat text-center mt-2">
              Separador horizontal
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default DocContentBuilder;
